import { useSortable } from '@dnd-kit/sortable';
import { CornerDownRight, GripVertical } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { LinkifiedText } from '@/components/LinkifiedText';
import { Checkbox } from '@/components/shadcn-ui/checkbox';
import type { Goal } from '@/entities/goal';
import { useCursorPosition } from '@/hooks/useCursorPosition';
import { useDebouncedGoalUpdate } from '@/queries/goal';
import { useUrlOverlay } from './useUrlOverlay';

interface GoalItemProps {
  index: number;
  goal: Goal;
  parentGoalTitle?: string;
  autoFocus?: boolean;
  focusCursorPosition?: number;
  period: string;
  startAtLocal: string;
  position: number;
  showPlaceholder?: boolean;
  onToggle: (checked: boolean, goalId: string) => void;
  onInsertGoal: (
    insertAtPosition: number,
    newGoalTitle: string,
    options?: { parentGoalId?: string; focusCurrentGoalId?: string },
  ) => void;
  onBackspaceAtStart: (goalId: string, currentTitle: string) => void;
  onDeleteAtEnd: (goalId: string, currentTitle: string) => void;
  onFocusNext: (currentGoalId: string, cursorPosition: number) => void;
  onFocusPrevious: (currentGoalId: string, cursorPosition: number) => void;
  onFocused: (goalId: string) => void;
  onMove: (goalId: string, direction: 'up' | 'down') => void;
  onPasteGoals: (
    insertAtPosition: number,
    titles: string[],
    parentGoalId?: string,
  ) => void;
  dropIndicator?: 'above' | 'below';
}

export const GoalItem = React.memo(function GoalItem({
  index,
  goal,
  parentGoalTitle,
  autoFocus,
  focusCursorPosition,
  period,
  startAtLocal,
  position,
  showPlaceholder = false,
  onToggle,
  onInsertGoal,
  onBackspaceAtStart,
  onDeleteAtEnd,
  onFocusNext,
  onFocusPrevious,
  onFocused,
  onMove,
  onPasteGoals,
  dropIndicator,
}: GoalItemProps) {
  const { attributes, listeners, setNodeRef } = useSortable({ id: goal.id });

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingCursorRestore = useRef<number | undefined>(undefined);
  const isLocalChange = useRef(false);

  const [title, setTitle] = useState(goal.title);
  const [lastExternalTitle, setLastExternalTitle] = useState(goal.title);

  const { isFocused, hasUrls, handleFocus, handleBlur, handleOverlayClick } =
    useUrlOverlay(title, textareaRef);

  if (goal.title !== lastExternalTitle) {
    setLastExternalTitle(goal.title);
    if (title === lastExternalTitle) {
      setTitle(goal.title);
    }
  }

  const { updateTitle } = useDebouncedGoalUpdate(goal.id, {
    period,
    startAtLocal,
    position,
  });

  useCursorPosition(textareaRef, {
    autoFocus,
    cursorPosition: focusCursorPosition,
    textLength: title.length,
  });

  useEffect(() => {
    if (!autoFocus || !textareaRef.current) {
      return;
    }
    if (document.activeElement === textareaRef.current) {
      onFocused(goal.id);
    }
  }, [autoFocus, goal.id, onFocused]);

  useEffect(() => {
    if (focusCursorPosition !== undefined) {
      pendingCursorRestore.current = focusCursorPosition;
    }
  }, [focusCursorPosition]);

  useLayoutEffect(() => {
    if (isLocalChange.current) {
      isLocalChange.current = false;
      return;
    }
    if (pendingCursorRestore.current == null || !textareaRef.current) {
      return;
    }
    const pos = Math.min(pendingCursorRestore.current, title.length);
    textareaRef.current.setSelectionRange(pos, pos);
    pendingCursorRestore.current = undefined;
  }, [title]);

  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      isLocalChange.current = true;
      const value = e.target.value;
      setTitle(value);
      updateTitle(value);
    },
    [updateTitle],
  );

  const handleToggle = useCallback(
    (checked: boolean) => {
      onToggle(checked, goal.id);
    },
    [goal.id, onToggle],
  );

  const handleFocusNext = useCallback(() => {
    const currentPosition = textareaRef.current?.selectionStart ?? 0;
    onFocusNext(goal.id, currentPosition);
  }, [onFocusNext, goal.id]);

  const handleFocusPrevious = useCallback(() => {
    const currentPosition = textareaRef.current?.selectionStart ?? 0;
    onFocusPrevious(goal.id, currentPosition);
  }, [onFocusPrevious, goal.id]);

  const handleEnter = useCallback(
    (value: string, cursorPos: number) => {
      if (value.trim() === '') {
        return;
      }
      if (cursorPos === value.length) {
        onInsertGoal(index + 1, '');
      } else if (cursorPos === 0) {
        onInsertGoal(index, '', { focusCurrentGoalId: goal.id });
      } else {
        const textBefore = value.slice(0, cursorPos);
        const textAfter = value.slice(cursorPos);
        isLocalChange.current = true;
        setTitle(textBefore);
        updateTitle(textBefore);
        onInsertGoal(index + 1, textAfter, {
          parentGoalId: goal.parentGoalId ?? undefined,
        });
      }
    },
    [goal.id, goal.parentGoalId, onInsertGoal, index, setTitle, updateTitle],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const keyMap: Record<string, () => void> = {
        ArrowUp: () => {
          e.preventDefault();
          handleFocusPrevious();
        },
        ArrowDown: () => {
          e.preventDefault();
          handleFocusNext();
        },
        'Alt+ArrowUp': () => {
          e.preventDefault();
          onMove(goal.id, 'up');
        },
        'Alt+ArrowDown': () => {
          e.preventDefault();
          onMove(goal.id, 'down');
        },
        Enter: () => {
          e.preventDefault();
          handleEnter(e.currentTarget.value, e.currentTarget.selectionStart);
        },
        'Alt+Enter': () => {
          e.preventDefault();
          handleToggle(goal.status !== 'done');
        },
        Backspace: () => {
          const { selectionStart, selectionEnd, value } = e.currentTarget;
          if (selectionStart === 0 && selectionEnd === 0) {
            e.preventDefault();
            onBackspaceAtStart(goal.id, value);
          }
        },
        Delete: () => {
          const { selectionStart, selectionEnd, value } = e.currentTarget;
          if (
            selectionStart === value.length &&
            selectionEnd === value.length
          ) {
            e.preventDefault();
            onDeleteAtEnd(goal.id, value);
          }
        },
      };

      const keyCombo = `${e.altKey ? 'Alt+' : ''}${e.key}`;
      const action = keyMap[keyCombo];

      if (action) {
        action();
      }
    },
    [
      goal.id,
      goal.status,
      onBackspaceAtStart,
      onDeleteAtEnd,
      onMove,
      handleEnter,
      handleToggle,
      handleFocusNext,
      handleFocusPrevious,
    ],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const pastedText = e.clipboardData.getData('text/plain');
      const lines = pastedText.split(/\r?\n/);

      if (lines.length <= 1) {
        return;
      }

      e.preventDefault();

      const { selectionStart, selectionEnd, value } = e.currentTarget;
      const textBefore = value.slice(0, selectionStart);
      const textAfter = value.slice(selectionEnd);

      const newTitle = textBefore + (lines[0] ?? '');
      const lastLine = lines[lines.length - 1] ?? '';
      const additionalTitles = [
        ...lines.slice(1, -1),
        lastLine + textAfter,
      ].filter((t) => t.trim() !== '');

      isLocalChange.current = true;
      setTitle(newTitle);
      updateTitle(newTitle);

      if (additionalTitles.length > 0) {
        onPasteGoals(
          index + 1,
          additionalTitles,
          goal.parentGoalId ?? undefined,
        );
      }
    },
    [goal.parentGoalId, index, onPasteGoals, updateTitle],
  );

  return (
    <li ref={setNodeRef} className="group relative flex items-start gap-2">
      {dropIndicator === 'above' && (
        <div
          data-testid="drop-indicator"
          className="absolute -top-[5px] right-0 left-0 h-[2px] bg-blue-500"
        />
      )}
      {dropIndicator === 'below' && (
        <div
          data-testid="drop-indicator"
          className="absolute right-0 -bottom-[5px] left-0 h-[2px] bg-blue-500"
        />
      )}
      <button
        type="button"
        className="absolute top-[2.5px] -left-6 cursor-grab opacity-0 group-hover:opacity-100 active:cursor-grabbing"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 text-gray-400" />
      </button>
      <Checkbox
        id={`goal-${goal.id}`}
        checked={goal.status === 'done'}
        onCheckedChange={handleToggle}
        disabled={title.trim() === ''}
        className="mt-[2.5px] cursor-pointer disabled:cursor-default"
      />
      <div className="flex min-w-0 flex-1 flex-col">
        {/* When the title contains URLs, the textarea is hidden behind a link overlay
            managed by useUrlOverlay. The textarea stays tabbable so keyboard focus
            enters edit mode naturally. See useUrlOverlay.ts for full details. */}
        <div className="relative flex min-w-0 flex-1 flex-col">
          <TextareaAutosize
            ref={textareaRef}
            value={title}
            placeholder={
              showPlaceholder
                ? period === 'day'
                  ? 'Add a task...'
                  : 'Add a goal...'
                : undefined
            }
            className={`placeholder:font-regular h-auto min-h-[1.5em] w-full resize-none overflow-hidden border-0 p-0 text-sm leading-normal font-light wrap-break-word whitespace-pre-wrap text-black outline-none placeholder:text-gray-400 hover:cursor-text focus:outline-none ${
              goal.status === 'done' ? 'text-gray-600' : ''
            } ${!isFocused && hasUrls ? 'pointer-events-none absolute inset-0 opacity-0' : ''}`}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
          {!isFocused && hasUrls && (
            <div
              className={`min-h-[1.5em] text-sm leading-normal font-light wrap-break-word whitespace-pre-wrap ${
                goal.status === 'done' ? 'text-gray-500' : 'text-black'
              }`}
              onClick={handleOverlayClick}
            >
              <LinkifiedText text={title} />
            </div>
          )}
        </div>
        {parentGoalTitle && (
          <div className="flex min-w-0 items-baseline gap-1">
            <CornerDownRight className="mt-[0.15em] h-3 w-3 shrink-0 text-gray-400" />
            <span
              className={`truncate text-xs ${goal.status === 'done' ? 'text-gray-400' : 'text-gray-500'}`}
            >
              {parentGoalTitle}
            </span>
          </div>
        )}
      </div>
    </li>
  );
});
